import type { ReactNode, SubmitEventHandler } from 'react';
import { useStepsContext } from '@/providers/pi-multi-step-form/StepsContext';
import { ArrowRight } from '@/components/pages/icons';
import Cta from '@/components/pages/cta';

type StepContainerProps = {
  title?: string;
  subtitle?: string;
  buttonLabel?: string;
  buttonDisabled?: boolean;
  hideButtonIcon?: boolean;
  onSubmit: () => void;
  children: ReactNode;
  footerNotice?: ReactNode;
};

export default function StepContainer({
  title,
  subtitle,
  buttonLabel,
  onSubmit,
  children,
  buttonDisabled,
  hideButtonIcon,
  footerNotice,
}: StepContainerProps) {
  const { progress, previousStep } = useStepsContext();

  const handleSubmit: SubmitEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (buttonDisabled) return;
    onSubmit();
  };

  return (
    <section className="w-full grow flex-1 flex flex-col animate-fade-in text-brand-text-button">
      {progress < 100 && (
        <>
          <p className="text-center text-sm font-medium leading-5 tracking-[0.21px] @lg:text-base @lg:leading-normal @lg:font-normal">
            Free case review
          </p>
          <div className="shrink-0 flex items-center gap-4">
            <button
              onClick={previousStep}
              className="text-[#2563EB] flex gap-2 items-center cursor-pointer hover:brightness-90"
            >
              <ArrowRight className="w-6 h-6 rotate-180" />
              <span className="sr-only">Back</span>
            </button>

            <div className="grow w-full h-2 bg-[#DFE2E4] relative rounded-full overflow-hidden">
              <div
                className="absolute top-0 left-0 h-full bg-[#2563EB] transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </>
      )}

      {title && (
        <div className="shrink-0 pt-8 w-full pb-4">
          <h2 className="text-[32px] font-normal leading-[120%] tracking-[-0.96px] @lg:text-5xl @lg:leading-[110%] @lg:tracking-[-1.44px]">
            {title}
          </h2>

          {subtitle && <p className="text-base font-normal leading-normal mt-2">{subtitle}</p>}
        </div>
      )}

      <form className="min-h-0 grow w-full flex flex-col" onSubmit={handleSubmit}>
        <div className="grow overflow-y-auto w-full">{children}</div>

        <div className="shrink-0 w-full pb-8 pt-4">
          <Cta
            variation="shadow"
            type="submit"
            size="md"
            disabled={buttonDisabled}
            icon={!hideButtonIcon ? <ArrowRight /> : undefined}
            text={buttonLabel || 'Continue'}
            className="w-full min-w-[212px] @lg:max-w-max @lg:mx-auto"
          />
          {footerNotice}
        </div>
      </form>
    </section>
  );
}
